import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Paper } from "@mui/material";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Layout, Text } from "$components";
import JTable from "$components/JTable/JTable";
import useStockList from "$hooks/Stock/useStockList";
import useJStyles from "$hooks/useJStyles";
import { appState } from "$reducers";
import useURLQuery from "$hooks/useURLQuery";
import useCompany from "$hooks/useCompany";
import { StockCompanySelect } from "./StockCompanySelect";
import { CompanyServices } from "$services";
import { CompanyType } from "$types";
import { CacheInvalidationAction } from "$utils/cache";
import useAuth from "$hooks/useAuth";

/** Convertit le format thead/bodyData legacy de useStockList en colonnes/données JTable. */
function StockTable({ tableData }: { tableData: any }) {
  const columns = useMemo(
    () =>
      (tableData.thead ?? []).map((col: any) => ({
        id: col.id,
        Header: col.label ?? col.id,
        Cell: ({ row }: any) => row.original[col.id],
      })),
    [tableData.thead]
  );

  const data = useMemo(
    () =>
      (tableData.bodyData ?? []).map((row: any[]) => {
        const obj: Record<string, any> = {};
        (tableData.thead ?? []).forEach((col: any, i: number) => {
          obj[col.id] = row[i]?.value ?? row[i] ?? null;
        });
        if (row[0]?.onClickAction) obj._onClick = row[0].onClickAction;
        return obj;
      }),
    [tableData.bodyData, tableData.thead]
  );

  return (
    <JTable
      data={data}
      columns={columns}
      setPageIndex={() => {}}
      hideCheckboxes
      hideFilters
      rowsPerPage={50}
      rowProps={(row) =>
        row.original._onClick
          ? { onClick: row.original._onClick, style: { cursor: "pointer" } }
          : {}
      }
    />
  );
}

function Stock() {
  const urlQuery = useURLQuery();
  const contributorId = urlQuery.get("contributor");
  const navigate = useNavigate();
  const { isClient, isContributor } = useCompany();
  // @todo: check with tristan la diff entre iscontributor usecompany et useauth
  const { isContributor: isContributorAuth } = useAuth();
  const { jclasses } = useJStyles();

  const { data: contributor } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, contributorId],
    queryFn: () => CompanyServices.getCompanyById(contributorId),
    enabled: !!contributorId,
    staleTime: 60 * 1000,
  });

  const { tableData, classes } = useStockList(contributor, {
    companyFilters: {
      $inCompanyTypes: [
        CompanyType.CLIENT,
        // CompanyType.CONTRIBUTOR,
        // CompanyType.LOGISTICIAN,
      ],
    },
    includeRelatedFiles: true,
  });
  const { filter: companySidebarFilter } = useSelector(appState);

  useEffect(() => {
    if (companySidebarFilter) {
      const { _id, companyTypes } = companySidebarFilter;
      if (isClient(companyTypes)) {
        navigate(`/stock/${companySidebarFilter._id}`);
      }
      if (isContributor(companyTypes) && !contributorId) {
        const params = new URLSearchParams();
        params.append("contributor", _id);
        navigate(`/stock?${params}`);
      }
    }
  }, [
    companySidebarFilter,
    contributor?.data,
    contributorId,
    navigate,
    isClient,
    isContributor,
  ]);

  return (
    <div className={jclasses.jWrapper}>
      <Layout
        title={`Liste des stocks${
          contributor
            ? ` client - ${contributor.name}`
            : isContributorAuth()
              ? ` client`
              : ""
        }`}
        renderHead={() => <StockCompanySelect />}
      >
        <Paper className={jclasses.jPaper}>
          <StockTable tableData={tableData} />
          {!tableData?.bodyData?.length && (
            <div className={classes.noResultPlaceHolder}>
              <Text type="h3">Aucune entreprise trouvée</Text>
            </div>
          )}
        </Paper>
      </Layout>
    </div>
  );
}

export default Stock;
